import { useCallback, useEffect, useRef, useState } from "react";
import quickBooksInvoicesApi, {
  isActiveInvoiceStatus,
} from "../services/quickBooksInvoicesApi";

const DEFAULT_POLL_DELAYS_MS = [1500, 3000, 6000, 12000, 30000];
const MAX_STATUS_FAILURES = 5;

export function useQuickBooksInvoiceStatus(shipmentId, options = {}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(Boolean(shipmentId));
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const generation = useRef(0);
  const lastCompletion = useRef(null);
  const liveRequest = useRef(null);
  const previousShipment = useRef(undefined);
  const callbackRef = useRef(options.onCompleted);
  callbackRef.current = options.onCompleted;

  const refresh = useCallback(() => setRefreshKey((value) => value + 1), []);

  useEffect(() => {
    const currentGeneration = ++generation.current;
    let timer;
    let stopped = false;
    let delayIndex = 0;
    let previousStatus;
    let consecutiveFailures = 0;

    const shipmentChanged = previousShipment.current !== shipmentId;
    previousShipment.current = shipmentId;
    if (shipmentChanged) {
      setStatus(null);
      lastCompletion.current = null;
      liveRequest.current = null;
    }
    setError(null);
    setLoading(Boolean(shipmentId));
    setPolling(false);

    if (!shipmentId || options.enabled === false) {
      setLoading(false);
      return undefined;
    }

    const current = () => !stopped && generation.current === currentGeneration;
    const applyStatus = (next) => {
      if (!current()) return;
      setStatus(next);
      if (next && isActiveInvoiceStatus(next.status)) {
        liveRequest.current = next.request_id;
      }
      if (
        next?.status === "succeeded" &&
        lastCompletion.current !== next.request_id
      ) {
        lastCompletion.current = next.request_id;
        callbackRef.current?.(next, {
          live: liveRequest.current === next.request_id,
        });
      }
    };

    const recoverRead = (nextError, again) => {
      if (!current()) return;
      setLoading(false);
      setError(nextError);
      const transient =
        nextError?.status !== 401 &&
        nextError?.status !== 403 &&
        (nextError?.status === 429 ||
          (nextError?.status >= 500 && nextError?.status <= 599));
      if (!transient || ++consecutiveFailures >= MAX_STATUS_FAILURES) {
        setPolling(false);
        return;
      }
      setPolling(true);
      const wait =
        DEFAULT_POLL_DELAYS_MS[
          Math.min(delayIndex++, DEFAULT_POLL_DELAYS_MS.length - 1)
        ];
      timer = setTimeout(() => {
        if (current()) again();
      }, wait);
    };

    const poll = async (requestId) => {
      if (!current()) return;
      setPolling(true);
      try {
        const next = await quickBooksInvoicesApi.getStatus(requestId);
        if (!current()) return;
        setError(null);
        consecutiveFailures = 0;
        applyStatus(next);
        if (next.status !== previousStatus) delayIndex = 0;
        previousStatus = next.status;
        if (isActiveInvoiceStatus(next.status)) {
          timer = setTimeout(
            () => void poll(requestId),
            DEFAULT_POLL_DELAYS_MS[
              Math.min(delayIndex++, DEFAULT_POLL_DELAYS_MS.length - 1)
            ],
          );
        } else {
          setPolling(false);
        }
      } catch (caught) {
        recoverRead(caught, () => void poll(requestId));
      }
    };

    const loadLatest = async () => {
      try {
        const latest =
          await quickBooksInvoicesApi.getLatestForShipment(shipmentId);
        if (!current()) return;
        setLoading(false);
        setError(null);
        consecutiveFailures = 0;
        applyStatus(latest);
        if (latest && isActiveInvoiceStatus(latest.status)) {
          void poll(latest.request_id);
        } else {
          setPolling(false);
        }
      } catch (caught) {
        recoverRead(caught, () => void loadLatest());
      }
    };

    void loadLatest();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [options.enabled, refreshKey, shipmentId]);

  const retry = useCallback(async () => {
    if (!status?.request_id || !status.can_retry || retrying || replacing) {
      return null;
    }
    setRetrying(true);
    try {
      const next = await quickBooksInvoicesApi.retry(status.request_id);
      liveRequest.current = next.request_id;
      setStatus(next);
      setError(null);
      refresh();
      return next;
    } catch (caught) {
      setError(caught);
      return null;
    } finally {
      setRetrying(false);
    }
  }, [refresh, replacing, retrying, status]);

  const replace = useCallback(async () => {
    if (!status?.request_id || !status.can_replace || replacing || retrying) {
      return null;
    }
    setReplacing(true);
    try {
      const next = await quickBooksInvoicesApi.replace(status.request_id);
      liveRequest.current = next.request_id;
      setStatus(next);
      setError(null);
      refresh();
      return next;
    } catch (caught) {
      setError(caught);
      return null;
    } finally {
      setReplacing(false);
    }
  }, [refresh, replacing, retrying, status]);

  return {
    status,
    loading,
    polling,
    retrying,
    replacing,
    error,
    refresh,
    retry,
    replace,
  };
}

export default useQuickBooksInvoiceStatus;
